<template>
    <div ref="box" class="trip-route-mini" :style="{height: height + 'px'}">
        <l-map
            v-if="latlngs.length"
            ref="map"
            :zoom="13"
            :center="latlngs[0]"
            :use-global-leaflet="false"
            :options="mapOptions"
            @ready="onReady">
            <l-tile-layer :url="mapConfig.streetUrl"/>
            <l-polyline v-for="(seg, i) in segments" :key="i" :lat-lngs="seg.latlngs"
                        :color="seg.color" :weight="4" :opacity="0.95"/>
            <l-circle-marker :lat-lng="latlngs[0]" :radius="5" color="#16A34A"
                             fill-color="#16A34A" :fill-opacity="1" :weight="2"/>
            <l-circle-marker :lat-lng="latlngs[latlngs.length - 1]" :radius="5" color="#EF4444"
                             fill-color="#EF4444" :fill-opacity="1" :weight="2"/>
        </l-map>
        <div v-else class="trip-route-mini__empty">No route recorded</div>
    </div>
</template>

<script setup>
import L from "leaflet";
import {mapConfig} from "@/composables/mapConfig";
import "leaflet/dist/leaflet.css";
import {ref, computed, watch, nextTick, onBeforeUnmount} from "vue";
import {LMap, LTileLayer, LPolyline, LCircleMarker} from "@vue-leaflet/vue-leaflet";
import {strOperations} from "@/composables/globals";

// A compact tiled route map for the Activity trip preview: shows the trip route
// on real map tiles (fed by the already-fetched route points) and frames it to
// fit. The full-screen dialog map (TripRouteMap) adds the animated replay.
const props = defineProps({
    routes: {type: Array, default: () => []},
    height: {type: Number, default: 190},
});

const map = ref(null);
const box = ref(null);
let fitTimer = null;
let ro = null;

// A static preview: no controls and no gestures, so it never fights the outer
// map when embedded in a Leaflet popup, and reads as a snapshot.
const mapOptions = {
    zoomControl: false,
    attributionControl: false,
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
    boxZoom: false,
    keyboard: false,
};

// Valid points with their speed, in order. Reject 0/0 and non-finite coords.
const validPts = computed(() => (props.routes || [])
    .map((r) => ({ll: [Number(r.latitude), Number(r.longitude)], mph: strOperations().speedToMph(r) || 0}))
    .filter((p) => Number.isFinite(p.ll[0]) && Number.isFinite(p.ll[1]) && (p.ll[0] || p.ll[1])));

const latlngs = computed(() => validPts.value.map((p) => p.ll));

// Absolute physical mph bands (same as the mobile app + full map), so a steady
// cruise doesn't read all-red. Takes the route point so it can use the
// server-resolved speed_mph; gps_speed alone is two different scales.
function speedColor(mphOrPoint) {
    const mph = typeof mphOrPoint === 'number'
        ? mphOrPoint
        : (strOperations().speedToMph(mphOrPoint) || 0);
    if (mph >= 70) return "#E53935"; // fast
    if (mph >= 50) return "#FB8C00"; // brisk
    return "#4CAF50";                // normal
}

// The route split into contiguous same-colour runs (one polyline each), coloured
// by the speed at each segment's end point.
const segments = computed(() => {
    const pts = validPts.value;
    if (pts.length < 2) return [];
    const segs = [];
    let seg = [pts[0].ll];
    let color = null;
    for (let i = 1; i < pts.length; i++) {
        const c = speedColor(pts[i].mph);
        if (color === null) color = c;
        if (c === color) {
            seg.push(pts[i].ll);
        } else {
            segs.push({latlngs: seg, color});
            seg = [pts[i - 1].ll, pts[i].ll];
            color = c;
        }
    }
    if (seg.length >= 2) segs.push({latlngs: seg, color});
    return segs;
});

// Recompute size + frame the route on the LIVE map object, re-read each time so a
// call that fires after the row collapsed (map destroyed) is a no-op.
function applyFit() {
    const lo = map.value?.leafletObject;
    if (!lo || !latlngs.value.length) return;
    lo.invalidateSize();
    const b = L.latLngBounds(latlngs.value);
    // fitBounds on a single-point bounds would zoom to max; clamp with maxZoom.
    lo.fitBounds(b, {padding: [24, 24], maxZoom: 17});
}

// Leaflet needs its size recomputed once the (expanding) container is laid out,
// otherwise it renders tiles for a zero/half box; a second pass covers the
// row-expand transition settling.
function fit() {
    nextTick(() => {
        applyFit();
        clearTimeout(fitTimer);
        fitTimer = setTimeout(applyFit, 220);
    });
}

const onReady = () => {
    fit();
    // Re-align tiles when the container resizes later (window/orientation
    // change) - only invalidateSize, not re-fit, so a resize doesn't reset a
    // view the user has panned.
    if (box.value && typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => map.value?.leafletObject?.invalidateSize());
        ro.observe(box.value);
    }
};
watch(() => props.routes, () => nextTick(fit));

onBeforeUnmount(() => {
    clearTimeout(fitTimer);
    if (ro) ro.disconnect();
});
</script>

<style scoped>
.trip-route-mini {
    position: relative;
    width: 100%;
    height: 190px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #e7ecf3;
}

.trip-route-mini :deep(.leaflet-container) {
    height: 100%;
    width: 100%;
    background: #e7ecf3;
}

.trip-route-mini__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #94a3b8;
}
</style>
